import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { toast } from "react-toastify";
import { getProducts } from "../../controllers/apiProducts";
import { getTaxCategories } from "../../controllers/apiConfig";

Modal.setAppElement("#root");

export default function ModalPreInvoiceItems({
  isOpen,
  onClose,
  preInvoice,
  onSave,
  validationRules = {},
}) {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxCategories, setTaxCategories] = useState([]);

  // =====================
  // PRODUCTS
  // =====================
  useEffect(() => {
    const fetch = async () => {
      const res = await getProducts();
      setProducts(
        res.map((p) => ({
          value: p.id,
          label: `${p.sku} - ${p.nombre}`,
          sku: p.sku,
          iva_categoria_id: p.iva_categoria_id || null,
          precio_base: parseFloat(p.precio_base) || 0,
        }))
      );
    };

    fetch();
  }, []);

  // =====================
  // TAX
  // =====================
  useEffect(() => {
    const fetch = async () => {
      // (2026-05-14) Items de proforma/factura pueden incluir IGTF como
      // pseudo-categoria — mantener la consulta adicional.
      const res = await getTaxCategories({ includeIgtf: true });
      setTaxCategories(res);
    };

    fetch();
  }, []);

  // =====================
  // LOAD ITEMS
  // =====================
  useEffect(() => {
    if (!preInvoice?.items) return;

    setItems(
      preInvoice.items.map((i) => ({
        ...i,
        iva_categoria_id: i.iva_categoria_id || null,
      }))
    );
  }, [preInvoice]);

  // =====================
  // MAP PRODUCTS (OPTIMIZADO)
  // =====================
  useEffect(() => {
    if (!items.length || !products.length) return;

    setItems((prev) =>
      prev.map((item) => {
        const match = products.find((p) => p.sku === item.producto_sku);

        return {
          ...item,
          productOption: match || null,
          producto_id: match?.value || null,
          iva_categoria_id:
            match?.iva_categoria_id ||
            item.iva_categoria_id ||
            taxCategories?.[0]?.id ||
            null,
          precio_unitario: item.precio_unitario || match?.precio_base || 0,
        };
      })
    );
  }, [products, taxCategories]);

  // =====================
  // CHANGE
  // =====================
  const handleChange = (idx, field, value) => {
    setItems((prev) => {
      const updated = [...prev];

      if (field === "productOption") {
        updated[idx].productOption = value;
        updated[idx].producto_sku = value?.sku || "";
        updated[idx].producto_id = value?.value || null;
        updated[idx].precio_unitario = value?.precio_base || 0;
        updated[idx].iva_categoria_id =
          value?.iva_categoria_id || taxCategories?.[0]?.id || null;
      } else {
        updated[idx][field] = value;
      }

      return updated;
    });
  };

  // =====================
  // ADD
  // =====================
  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        producto_sku: "",
        producto_id: null,
        cantidad: 1,
        precio_unitario: 0,
        descuento_porcentaje: 0,
        iva_categoria_id: taxCategories?.[0]?.id || null,
        productOption: null,
      },
    ]);
  };

  // =====================
  // DELETE
  // =====================
  const handleDeleteItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    toast.info("Item eliminado");
  };

  // =====================
  // VALIDATION
  // =====================
  const validateItems = () => {
    for (const item of items) {
      if (!item.iva_categoria_id) {
        toast.error("Hay items sin IVA asignado");
        return false;
      }
    }

    if (!validationRules.items) return true;

    const { valid, message } = validationRules.items(items);

    if (!valid) toast.error(message);

    return valid;
  };

  // =====================
  // SAVE
  // =====================
  const handleSave = () => {
    if (!validateItems()) return;

    const cleaned = items.map((item) => ({
      id: item.id || null,
      producto_id: item.producto_id,
      nombre: item.productOption?.label || item.nombre || "",
      cantidad: item.cantidad,
      precio_unitario: item.precio_unitario,
      aplica_iva: true,
      iva_categoria_id: item.iva_categoria_id || null,
      activo: true,
      descuento_porcentaje: item.descuento_porcentaje || 0,
    }));

    onSave(cleaned);
    onClose();
  };

  return isOpen ? (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="bg-white p-6 rounded-2xl max-w-6xl mx-auto mt-10 shadow-2xl overflow-visible"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start z-50 overflow-auto"
    >
      <h2 className="text-xl font-bold mb-4">
        Items de Pre-Factura {preInvoice?.correlativo_interno}
      </h2>

      <div className="overflow-x-auto max-h-[75vh]">
        <table className="min-w-full border border-gray-300 text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="border px-2 py-1 text-left">Producto</th>
              <th className="border px-2 py-1 text-center">Cantidad</th>
              <th className="border px-2 py-1 text-right">Precio Unitario</th>
              <th className="border px-2 py-1 text-center">Descuento %</th>
              <th className="border px-2 py-1 text-center">IVA</th>
              {/* <th className="border px-2 py-1 text-center">IVA ID</th> */}
              <th className="border px-2 py-1 text-center w-16">Acción</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item, idx) => {
              const ivaLabel =
                taxCategories.find((t) => t.id === item.iva_categoria_id)
                  ?.tasa_porcentaje || "0.00";

              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="border px-2 py-1">
                    <Select
                      options={products}
                      value={item.productOption}
                      onChange={(opt) => handleChange(idx, "productOption", opt)}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </td>

                  <td className="border px-2 py-1 text-center">
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-center"
                      value={item.cantidad || 1}
                      onChange={(e) =>
                        handleChange(idx, "cantidad", Number(e.target.value))
                      }
                    />
                  </td>

                  <td className="border px-2 py-1 text-right">
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-right"
                      value={item.precio_unitario || 0}
                      onChange={(e) =>
                        handleChange(idx, "precio_unitario", Number(e.target.value))
                      }
                    />
                  </td>

                  <td className="border px-2 py-1 text-center">
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-center"
                      value={item.descuento_porcentaje || 0}
                      onChange={(e) =>
                        handleChange(idx, "descuento_porcentaje", Number(e.target.value))
                      }
                    />
                  </td>

                  <td className="border px-2 py-1 text-center bg-gray-100">
                    {ivaLabel}
                  </td>

                  {/* <td className="border px-2 py-1 text-center bg-gray-100">
                    {item.iva_categoria_id || "N/A"}
                  </td> */}

                  <td className="border px-2 py-1 text-center">
                    <button
                      onClick={() => handleDeleteItem(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <i className="fa-regular fa-rectangle-xmark fa-lg"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <button
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          onClick={handleAddItem}
        >
          Agregar Item
        </button>

        <button
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
          onClick={handleSave}
        >
          Guardar
        </button>
      </div>
    </Modal>
  ) : null;
}